// src/App.js
import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import PageLoader from "./components/PageLoader";

// ── Lazy imports — all files must have a default export ───────────────────────
const Layout                      = React.lazy(() => import("./components/Layout"));
const Login                       = React.lazy(() => import("./pages/login"));
const ForgotPassword              = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword               = React.lazy(() => import("./pages/ResetPassword"));
const NotFound                    = React.lazy(() => import("./pages/NotFound"));
const Home                        = React.lazy(() => import("./pages/Home"));
const Reporting                   = React.lazy(() => import("./pages/Reporting"));
const Analytics                   = React.lazy(() => import("./pages/Analytics"));
const Disclosures                 = React.lazy(() => import("./pages/Disclosures"));
const GHGAnalytics                = React.lazy(() => import("./pages/GHGAnalytics"));
const SubmissionAnalytics         = React.lazy(() => import("./pages/SubmissionAnalytics"));
const AdminPortal                 = React.lazy(() => import("./pages/AdminPortal"));
const AccountRequests             = React.lazy(() => import("./pages/AccountRequests"));
const ManageUsers                 = React.lazy(() => import("./pages/ManageUsers"));
const BuildingsUpload             = React.lazy(() => import("./pages/BuildingsUpload"));
const BuildingsInformationForm    = React.lazy(() => import("./pages/BuildingsInformationForm"));
const HistoricEmissionsManagement = React.lazy(() => import("./pages/HistoricEmissionsManagement"));
const Policy                      = React.lazy(() => import("./pages/Policy"));
const Resources                   = React.lazy(() => import("./pages/Resources"));
const RoadToNetZero               = React.lazy(() => import("./pages/RoadToNetZero"));
const News                        = React.lazy(() => import("./pages/News"));
const NewsArticleTemplate         = React.lazy(() => import("./pages/NewsArticleTemplate"));
const Procurement                 = React.lazy(() => import("./pages/Procurement"));
const EmissionFactorsRegistery    = React.lazy(() => import("./components/EmissionFactorsRegistery"));
const Assets                      = React.lazy(() => import("./pages/Assets/Assets"));
const AssetBuildings              = React.lazy(() => import("./pages/Assets/Buildings"));
const AssetEnergyNetwork          = React.lazy(() => import("./pages/Assets/EnergyNetwork"));
const AssetPlaceholder            = React.lazy(() => import("./pages/Assets/Placeholder"));
const HomeComponent               = React.lazy(() => import("./components/HomeComponent"));
const Buildings                   = React.lazy(() => import("./components/Buildings"));
const BuildingSubmissionForm      = React.lazy(() => import("./components/BuildingSubmissionForm"));
const EnergyNetworkSubmissionForm = React.lazy(() => import("./components/EnergyNetworkSubmissionForm"));
const ViewEditBuildingInformation = React.lazy(() => import("./components/ViewEditBuildingInformation"));
const Vehicles                    = React.lazy(() => import("./components/Vehicles"));
const VehicleSubmissions          = React.lazy(() => import("./components/VehicleSubmissions"));
const Travel                      = React.lazy(() => import("./components/Travel"));
const TravelSubmissions           = React.lazy(() => import("./components/TravelSubmissions"));
const IndustrialProcesses         = React.lazy(() => import("./components/IndustrialProcesses"));
const WasteManagement             = React.lazy(() => import("./components/WasteManagement"));
const CreateUserAccount           = React.lazy(() => import("./components/CreateUserAccount"));
const GHGMap                      = React.lazy(() => import("./components/GHGMap"));
const GHGMapBuildings             = React.lazy(() => import("./components/GHGMapBuildings"));
const NetZeroPerformance          = React.lazy(() => import("./components/NetZeroPerformance"));
const NewsUpload                  = React.lazy(() => import("./components/NewsUpload"));
const TermsOfServices             = React.lazy(() => import("./pages/TermsOfServices"));
const PrivacyPolicy               = React.lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy                = React.lazy(() => import("./pages/CookiePolicy"));

// ── Private route ─────────────────────────────────────────────────────────────
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  return user ? children : <Navigate to="/login" replace />;
};

// ── Routes ────────────────────────────────────────────────────────────────────
function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public */}
        <Route path="/login"                        element={<Login />} />
        <Route path="/signup"                       element={<CreateUserAccount />} />
        <Route path="/forgot-password"              element={<ForgotPassword />} />
        <Route path="/reset-password/:uid/:token"   element={<ResetPassword />} />

        {/* Protected — everything inside Layout */}
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Home />} />

          <Route path="news"           element={<News />} />
          <Route path="news/upload"    element={<NewsUpload />} />
          <Route path="news/id/:id"    element={<NewsArticleTemplate />} />
          <Route path="news/:slug"     element={<NewsArticleTemplate />} />

          <Route path="reporting" element={<Reporting />}>
            <Route index                                    element={<HomeComponent />} />
            <Route path="buildings"                        element={<Buildings />} />
            <Route path="buildings/submit"                 element={<BuildingSubmissionForm />} />
            <Route path="buildings/networks/submit"        element={<EnergyNetworkSubmissionForm />} />
            <Route path="buildings/view-edit-info/:id"     element={<ViewEditBuildingInformation />} />
            <Route path="buildings/add-info"               element={<BuildingsInformationForm />} />
            <Route path="vehicles"                         element={<Vehicles />} />
            <Route path="vehicles/submissions"             element={<VehicleSubmissions />} />
            <Route path="travel"                           element={<Travel />} />
            <Route path="travel/submissions"               element={<TravelSubmissions />} />
            <Route path="industrial-processes"             element={<IndustrialProcesses />} />
            <Route path="waste-management"                 element={<WasteManagement />} />
            <Route path="procurement"                      element={<Procurement />} />
            <Route path="emissionfactorsregistry"          element={<EmissionFactorsRegistery />} />
          </Route>

          <Route path="assets"                       element={<Assets />} />
          <Route path="assets/buildings"             element={<AssetBuildings />} />
          <Route path="assets/buildings/networks"    element={<AssetEnergyNetwork />} />
          <Route path="assets/buildings/add"         element={<BuildingsInformationForm />} />
          <Route path="assets/buildings/view/:id"    element={<ViewEditBuildingInformation />} />
          <Route path="assets/vehicles"              element={<AssetPlaceholder />} />
          <Route path="assets/land"                  element={<AssetPlaceholder />} />
          <Route path="assets/refrigeration-cooling" element={<AssetPlaceholder />} />
          <Route path="assets/agricultural-assets"   element={<AssetPlaceholder />} />
          <Route path="assets/generators"            element={<AssetPlaceholder />} />
          <Route path="assets/energy-generation"     element={<AssetPlaceholder />} />
          <Route path="assets/investments"           element={<AssetPlaceholder />} />
          <Route path="assets/loan-book"             element={<AssetPlaceholder />} />

          <Route path="analytics"            element={<Analytics />} />
          <Route path="disclosures"          element={<Disclosures />} />
          <Route path="ghganalytics"         element={<GHGAnalytics />} />
          <Route path="submissionanalytics"  element={<SubmissionAnalytics />} />
          <Route path="ghgmap"               element={<GHGMap />} />
          <Route path="ghgmapbuildings"      element={<GHGMapBuildings />} />
          <Route path="netzeroperformance"   element={<NetZeroPerformance />} />
          <Route path="roadtonetzero"        element={<RoadToNetZero />} />
          <Route path="adminportal"          element={<AdminPortal />} />
          <Route path="account-requests"     element={<AccountRequests />} />
          <Route path="manage-users"         element={<ManageUsers />} />
          <Route path="buildings-upload"     element={<BuildingsUpload />} />
          <Route path="historic-emissions"   element={<HistoricEmissionsManagement />} />
          <Route path="policy"               element={<Policy />} />
          <Route path="resources"            element={<Resources />} />

          {/* Legal pages */}
          <Route path="terms-of-services" element={<TermsOfServices />} />
          <Route path="privacy-policy"    element={<PrivacyPolicy />} />
          <Route path="cookie-notice"     element={<CookiePolicy />} />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

// ── Root export ───────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
